import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Grid, List, ListItem, Typography } from '@mui/material';
import { Collapsible } from '../collapsible/collapsible';
import { Button } from '../button/button';
import { Assessment } from '../../state/reduxRepositories/assessments/assessments.interfaces';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { dateTime } from '../../time/datetime';
import { getRouteByAccountType } from '../../routes/getRouteByAccountType';
import { LoggedInUser } from '../../state/helperServices/auth/auth.interfaces';
import { SymptomAsessmentDetail } from './symptomAssessmentDetail';

interface SymptomAssessmentListProps {
  enableAssessment: boolean;
  user: LoggedInUser;
  player: Player;
  symptomChecksByPlayer: Assessment[];
}

export function SymptomAssessmentList({
  enableAssessment,
  user,
  player,
  symptomChecksByPlayer,
}: SymptomAssessmentListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Assessment>();

  const latestSymptomCheck = symptomChecksByPlayer[0] ?? null;
  const playerFirstName = player.firstName;

  const handleOnCreateSymptom = (playerId: string) => {
    navigate(
      `/${getRouteByAccountType(user.accountType)}/create-symptom-check`,
      { state: { selectedPlayerId: playerId } },
    );
  };

  const displayDetails = (assessmentId: string) => {
    setSelected(
      symptomChecksByPlayer.find(
        (assessment) => assessment.id === assessmentId,
      ),
    );
  };

  const closeDetails = () => {
    setSelected(undefined);
  };

  return (
    <>
      <Collapsible title={t('symptom.SYMPTOM_CHECK_CARD_TITLE')}>
        {selected && (
          <SymptomAsessmentDetail
            onClose={closeDetails}
            symptomAsessment={selected}
          />
        )}

        {!selected && (
          <>
            {symptomChecksByPlayer.map((symptomCheck) => (
              <div key={symptomCheck.id}>
                <Grid container justifyContent="space-between">
                  <Grid item sx={{ my: 1 }}>
                    <Typography variant="subtitle1">
                      {t('symptom.SYMPTOM_CHECK_CARD_ROW_TITLE_DATE_AND_TIME')}
                    </Typography>
                  </Grid>
                  <Grid item sx={{ my: 1 }}>
                    {dateTime.format(
                      symptomCheck.originalCreatedAt ?? symptomCheck.createdAt,
                      t('common.datetime.FORMAT_STANDARD_DATE_TIME'),
                    )}
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between">
                  <Grid item sx={{ my: 1 }}>
                    <Typography variant="subtitle1">
                      {t('symptom.SYMPTOM_CHECK_CARD_ROW_TITLE_SCORE')}
                    </Typography>
                  </Grid>
                  <Grid item sx={{ my: 1 }}>
                    {`${symptomCheck.assessment.assessment.score}/${symptomCheck.assessment.assessment.totalSteps}`}
                  </Grid>
                </Grid>

                <Button
                  sx={{ textTransform: 'none', pl: 0 }}
                  type="submit"
                  variant="text"
                  onClick={() => displayDetails(symptomCheck.id)}
                  label={t('symptom.SYMPTOM_CHECK_CARD_RESULTS')}
                />
                <hr />
              </div>
            ))}

            {enableAssessment && latestSymptomCheck && (
              <Button
                sx={{ mt: 2 }}
                type="submit"
                fullWidth
                color="secondary"
                variant="contained"
                onClick={() => handleOnCreateSymptom(player.id)}
                label={t('symptom.SYMPTOM_CHECK_CARD_ANOTHER_CHECK')}
              />
            )}

            {!latestSymptomCheck && (
              <>
                <List
                  sx={{
                    listStyleType: 'disc',
                    pl: 4,
                    '& .MuiListItem-root': {
                      display: 'list-item',
                    },
                  }}
                >
                  <ListItem>
                    <Typography variant="body1">
                      {t('symptom.SYMPTOM_CHECK_CARD_PROMPT', {
                        playerFirstName,
                      })}
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Typography variant="body1">
                      {t('symptom.SYMPTOM_CHECK_CARD_PROMPT2', {
                        playerFirstName,
                      })}
                    </Typography>
                  </ListItem>
                </List>

                {enableAssessment && (
                  <Button
                    sx={{ mt: 2 }}
                    type="submit"
                    fullWidth
                    color="secondary"
                    variant="contained"
                    onClick={() => handleOnCreateSymptom(player.id)}
                    label={t('symptom.SYMPTOM_CHECK_CARD_CHECK')}
                  />
                )}
              </>
            )}
          </>
        )}
      </Collapsible>
    </>
  );
}
